/**
 * Dealer Button Move Effect for Poker Reborn Effects Engine
 *
 * Animates the dealer button moving between seats.
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - P4.4.2
 */

import { Container } from 'pixi.js';
import { TimedEffect } from '../../core/TimedEffect';
import type { EffectTarget } from '../../core/EffectTypes';
import { EffectPriority } from '../../core/EffectTypes';
import type { EffectOptions } from '../../core/Effect';
import { easeInOutCubic } from '../../utils/easing';

// =============================================================================
// TYPES
// =============================================================================

export interface DealerButtonMoveData {
  /** The dealer button component */
  component: Container;
  /** Starting X position */
  fromX: number;
  /** Starting Y position */
  fromY: number;
  /** Ending X position */
  toX: number;
  /** Ending Y position */
  toY: number;
  /** Arc height for curved path */
  arcHeight?: number | undefined;
}

// =============================================================================
// DEFAULTS
// =============================================================================

const DEFAULT_DURATION = 400; // ms
const DEFAULT_ARC_HEIGHT = 20;

// =============================================================================
// DEALER BUTTON MOVE EFFECT (P4.4.2)
// =============================================================================

/**
 * Moves the dealer button with a slight arc for visual interest.
 */
export class DealerButtonMoveEffect extends TimedEffect {
  private readonly moveData: DealerButtonMoveData;

  constructor(
    target: EffectTarget,
    data: DealerButtonMoveData,
    options: EffectOptions = {}
  ) {
    super('DealerButtonMove', target, options.duration ?? DEFAULT_DURATION, {
      ...options,
      priority: options.priority ?? EffectPriority.NORMAL,
    });

    this.moveData = data;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  protected override onPlay(): void {
    const { component, fromX, fromY } = this.moveData;

    // Ensure starting position
    component.x = fromX;
    component.y = fromY;
    component.visible = true;

    super.onPlay();
  }

  protected onTick(progress: number): void {
    const { component, fromX, fromY, toX, toY, arcHeight } = this.moveData;

    // Apply easing
    const eased = easeInOutCubic(progress);

    // Linear interpolation for X
    component.x = fromX + (toX - fromX) * eased;

    // Y with arc
    const linearY = fromY + (toY - fromY) * eased;
    const arc = arcHeight ?? DEFAULT_ARC_HEIGHT;
    const arcOffset = -arc * 4 * progress * (1 - progress);
    component.y = linearY + arcOffset;

    // Slight rotation during movement
    component.rotation = Math.sin(progress * Math.PI) * 0.1;
  }

  protected override onCancel(): void {
    super.onCancel();

    // Snap to end position
    const { component, toX, toY } = this.moveData;
    component.x = toX;
    component.y = toY;
    component.rotation = 0;
  }
}

// =============================================================================
// FACTORY FUNCTION
// =============================================================================

/**
 * Create a DealerButtonMoveEffect from cue data.
 */
export function createDealerButtonMoveEffect(
  target: EffectTarget,
  data: Record<string, unknown>,
  options: EffectOptions = {}
): DealerButtonMoveEffect | null {
  const component = data['component'];
  if (!(component instanceof Container)) {
    return null;
  }

  const moveData: DealerButtonMoveData = {
    component,
    fromX: (data['fromX'] as number) ?? 0,
    fromY: (data['fromY'] as number) ?? 0,
    toX: (data['toX'] as number) ?? 0,
    toY: (data['toY'] as number) ?? 0,
    arcHeight: data['arcHeight'] as number | undefined,
  };

  return new DealerButtonMoveEffect(target, moveData, options);
}
